import { useGetCommercialBookingQuery } from "@/services/commercialBooking";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import AvailabilitySlotGrid from "./AvailabilitySlotGrid";
import { faClock, faUser, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./booking.css";

export interface CommercialBookingSelection {
  startAt: string;
  proposalToken?: string;
  ready: boolean;
}
interface Props {
  companyId: string;
  meetingId?: string;
  initialStartAt?: string;
  onChange: (selection: CommercialBookingSelection) => void;
  refreshKey: number;
}
const reasons = {
  "inactive-owner": "Commercial inactif : saisissez l'heure du rendez-vous.",
  "no-assignment-path":
    "Aucune attribution commerciale disponible : saisissez l'heure du rendez-vous.",
  "move-without-owner":
    "Ce rendez-vous n'a pas de commercial : son déplacement conserve cette situation.",
  "no-available-manager": "Aucun commercial disponible pour ce jour.",
};

export default function CommercialBookingFields({
  companyId,
  meetingId,
  initialStartAt,
  onChange,
  refreshKey,
}: Props) {
  const initial = initialStartAt
    ? DateTime.fromISO(initialStartAt, { zone: "Europe/Paris" }).setZone(
        "Europe/Paris",
      )
    : null;
  // Naive calendar seeds describe Paris wall time; reject normalization across
  // a spring gap. Offset-bearing stored instants still convert to Paris normally.
  const naiveInitial = initialStartAt?.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/,
  )?.[1];
  const validInitial =
    initial?.isValid &&
    (!naiveInitial || initial.toFormat("yyyy-MM-dd'T'HH:mm") === naiveInitial);
  const [date, setDate] = useState(
    initial?.toISODate() ?? DateTime.now().setZone("Europe/Paris").toISODate()!,
  );
  const context = `${companyId}:${meetingId ?? ""}:${date}:${refreshKey}`;
  const initialContext = useRef(context);
  const [selection, setSelection] = useState<{
    context: string;
    hour: string;
    token?: string | null;
  } | null>(
    validInitial && initial
      ? { context, hour: initial.toFormat("HH:mm") }
      : null,
  );
  const { currentData, isFetching, isError, refetch } =
    useGetCommercialBookingQuery(
      { companyId, date, meetingId },
      { skip: !date, refetchOnMountOrArgChange: true, refetchOnFocus: true },
    );
  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    if (lastRefresh.current !== refreshKey) {
      lastRefresh.current = refreshKey;
      setSelection(null);
      if (date) refetch();
    }
  }, [refreshKey, date, refetch]);
  useEffect(() => {
    if (selection && selection.context !== context) setSelection(null);
  }, [context, selection]);
  // currentData belongs to these exact RTK arguments; data could still name the previous owner.
  const preview =
    currentData?.companyId === companyId &&
    currentData.date === date &&
    currentData.meetingId === (meetingId ?? null)
      ? currentData
      : undefined;
  const hour =
    selection?.context === context &&
    (selection.token === preview?.proposalToken ||
      (selection.token === undefined && initialContext.current === context))
      ? selection.hour
      : "";
  const wallTime = `${date}T${hour}`;
  const parsedStart = DateTime.fromISO(wallTime, { zone: "Europe/Paris" });
  // isValid alone admits nonexistent hours by moving them forward silently.
  const validWallTime =
    parsedStart.isValid &&
    parsedStart.toFormat("yyyy-MM-dd'T'HH:mm") === wallTime;
  const invalidWallTime = !!hour && !validWallTime;
  const permitted =
    !!hour &&
    validWallTime &&
    parsedStart > DateTime.now() &&
    !!preview &&
    (preview.mode === "free-form" ||
      (preview.mode === "slots" &&
        (preview.allowOverride ||
          preview.slots.some((s) => s.start === hour && s.state === "free"))));
  const ready =
    !isFetching && !isError && !!preview?.proposalToken && permitted;
  const startAt = ready ? `${date}T${hour}` : "";
  const proposalToken = ready
    ? (preview?.proposalToken ?? undefined)
    : undefined;
  useEffect(() => {
    onChange({ startAt, proposalToken, ready });
  }, [startAt, proposalToken, ready, onChange]);
  // Consume the initial calendar click once; invalid or changed contexts never resurrect it.
  useEffect(() => {
    if (!isFetching && preview && selection?.context === context) {
      if (selection.token === undefined)
        setSelection(
          permitted ? { ...selection, token: preview.proposalToken } : null,
        );
      else if (selection.token !== preview.proposalToken) setSelection(null);
    }
  }, [isFetching, preview, selection, context, permitted]);
  const choose = (value: string) =>
    setSelection({ context, hour: value, token: preview?.proposalToken });
  const ownerName = preview?.owner
    ? `${preview.owner.firstname} ${preview.owner.lastname}`.trim()
    : "Aucun commercial attribué";
  const ownerInitials = preview?.owner
    ? `${preview.owner.firstname?.[0] ?? ""}${preview.owner.lastname?.[0] ?? ""}`.toLocaleUpperCase("fr")
    : "";
  return (
    <section
      className="commercial-booking mb-3"
      aria-label="Créneau du rendez-vous commercial"
    >
      <div className="commercial-booking__context">
        <div
          className="commercial-booking__owner"
          aria-label="Commercial"
          aria-live="polite"
          aria-busy={isFetching}
        >
          {isFetching ? (
            <div role="status" className="commercial-booking__loading">
              <span
                className="commercial-booking__placeholder"
                aria-hidden="true"
              />
              Chargement des créneaux…
            </div>
          ) : !isError && preview ? (
            <>
              <span className="commercial-booking__avatar" aria-hidden="true">
                {ownerInitials || <FontAwesomeIcon icon={faUser} />}
              </span>
              <div className="commercial-booking__identity">
                <h3 className="commercial-booking__name">{ownerName}</h3>
                {preview.owner && (
                  <p className="commercial-booking__assignment">
                    {preview.source === "rotation"
                      ? "Commercial proposé selon la rotation de l’équipe"
                      : preview.source === "company"
                        ? "Commercial rattaché à cette entreprise"
                        : "Commercial"}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
        <Form.Group
          className="commercial-booking__day"
          controlId="commercial-booking-day"
        >
          <Form.Label>
            Jour <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            aria-label="Jour du rendez-vous"
            value={date}
            onChange={(e) => {
              onChange({ startAt: "", ready: false });
              setSelection(null);
              setDate(e.target.value);
            }}
          />
        </Form.Group>
      </div>
      {isError ? (
        <div role="alert" className="commercial-booking__notice">
          Impossible de charger les créneaux.{" "}
          <Button
            variant="link"
            onClick={() => {
              onChange({ startAt: "", ready: false });
              setSelection(null);
              refetch();
            }}
          >
            Recharger les créneaux
          </Button>
        </div>
      ) : (
        preview && (
          <div
            className="commercial-booking__availability"
            aria-busy={isFetching}
          >
            {preview.reason && <p role="status">{reasons[preview.reason]}</p>}
            {preview.mode === "slots" && (
              <>
                <div className="commercial-booking__heading">
                  <h3>Choisir un créneau</h3>
                  <span className="commercial-booking__duration">
                    <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                    {preview.durationMinutes} min · Heure de Paris
                  </span>
                </div>
                <p className="commercial-booking__hint">
                  Cliquez sur un créneau libre pour choisir l'heure du
                  rendez-vous.
                </p>
                {!preview.slots.length && (
                  <p role="status">Aucun créneau disponible pour ce jour.</p>
                )}
                <AvailabilitySlotGrid
                  items={preview.slots.map((s) => ({
                    ...s,
                    selectable:
                      !isFetching &&
                      (s.state === "free" || preview.allowOverride),
                  }))}
                  selectedHour={permitted ? hour : null}
                  onSelect={choose}
                />
              </>
            )}
            {(preview.mode === "free-form" ||
              (preview.mode === "slots" && preview.allowOverride)) && (
              <Form.Group
                controlId="commercial-booking-hour"
                className="commercial-booking__manual"
              >
                <Form.Label>
                  {preview.allowOverride
                    ? "Heure du rendez-vous (dérogation autorisée)"
                    : "Heure du rendez-vous"}
                </Form.Label>
                <Form.Control
                  type="time"
                  aria-label="Heure du rendez-vous"
                  value={hour}
                  isInvalid={invalidWallTime}
                  aria-invalid={invalidWallTime || undefined}
                  aria-describedby={
                    invalidWallTime
                      ? "commercial-booking-hour-error"
                      : undefined
                  }
                  disabled={isFetching}
                  onChange={(e) => choose(e.target.value)}
                />
                {invalidWallTime && (
                  <Form.Control.Feedback
                    type="invalid"
                    id="commercial-booking-hour-error"
                  >
                    L'heure choisie n'existe pas à Paris ce jour-là (changement
                    d'heure). Choisissez une autre heure.
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            )}
            {ready && (
              <p className="commercial-booking__selection" role="status">
                <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
                <span>
                  Créneau retenu : <strong>{hour}</strong>.
                </span>
              </p>
            )}
          </div>
        )
      )}
    </section>
  );
}
